// Lab Campaign Dry-Run — pair a saved Segment with a LabEmailTemplate's
// content_selection_config and show, per sample member, exactly which
// articles would be inlined into the email if the campaign fired right now.
//
// No real send. No suppression footprint. Reproducible (deterministic
// sample by subscriber id) so you can re-run after editing a template
// or segment and see whether the picks actually changed.

import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { Panel } from '../components/Panel'
import { Button } from '../components/Button'
import { api, type Segment } from '../api/client'

const PUB_NAMES: Record<string, string> = {
  rc_car_action: 'RC Car Action',
  flight_journal: 'Flight Journal',
  model_airplane_news: 'Model Airplane News',
  diecast: 'Die Cast X',
}

type LabTemplateRow = {
  id: number
  slug: string
  name: string
  purpose: string
  publication_id: number | null
  enabled: boolean
  has_selection_config: boolean
}

type DryRunMember = {
  subscriber_id: number
  email: string
  article_ids: number[]
}

type DryRunOut = {
  segment_id: number
  lab_template_id: number
  audience_size: number
  sample: DryRunMember[]
}

type ArticleOut = {
  id: number
  publication_slug: string
  title: string
  wp_url: string
  featured_image_url: string | null
  story_type: string | null
  era: string | null
  ai_tags: string[] | null
  short_teaser: string | null
  engagement_score: number
}

export function LabCampaignDryRunPage() {
  const [segmentId, setSegmentId] = useState<number | null>(null)
  const [templateId, setTemplateId] = useState<number | null>(null)
  const [sampleSize, setSampleSize] = useState<number>(3)
  const [submitted, setSubmitted] = useState<{
    segmentId: number
    templateId: number
    sampleSize: number
  } | null>(null)

  const segments = useQuery({
    queryKey: ['segments-list'],
    // /api/segments returns { items: Segment[] } — must unwrap or .map crashes
    // the render and unmounts the page (the symptom: "page shows then disappears").
    queryFn: async () => {
      const resp = await api.get<{ items: Segment[] }>('/api/segments')
      return resp.items
    },
    staleTime: 60_000,
  })
  const templates = useQuery({
    queryKey: ['lab', 'templates'],
    queryFn: () => api.get<LabTemplateRow[]>('/api/lab/templates'),
    staleTime: 60_000,
  })

  const dryRun = useQuery({
    queryKey: ['lab', 'dry-run', submitted],
    queryFn: () =>
      api.post<DryRunOut>('/api/lab/campaigns/dry-run', {
        segment_id: submitted!.segmentId,
        lab_template_id: submitted!.templateId,
        sample_size: submitted!.sampleSize,
      }),
    enabled: submitted != null,
    retry: false,
  })

  // Fetch the article details for IDs returned by the dry-run, so we can
  // render full cards instead of bare IDs. We re-use /api/lab/content/preview
  // with a one-off config that just whitelists the IDs we got back: the
  // simplest thing here is to call it once per unique subscriber, but that's
  // wasteful — instead we hit the existing /api/lab/archive endpoint, which
  // can fetch in bulk by reading the page-list and merging client-side.
  // For now, fetch each unique article via /api/lab/archive and let the
  // browser cache the responses. Could be optimized later with a bulk hydrate.
  const articleIds = useMemo(() => {
    if (!dryRun.data) return [] as number[]
    const set = new Set<number>()
    for (const m of dryRun.data.sample) for (const id of m.article_ids) set.add(id)
    return [...set]
  }, [dryRun.data])

  // One bulk fetch per article is dumb for 30+ items; instead we fetch the
  // archive list filtered by the dry-run's segment publication when we have
  // it. Cleaner: a future /api/lab/archive/by-ids endpoint. For now we hydrate
  // by page-walking the archive endpoint and matching IDs.
  const articleMap = useArticleMap(articleIds)

  const canSubmit =
    segmentId != null && templateId != null && sampleSize >= 1 && sampleSize <= 10

  return (
    <div className="p-8 space-y-6 max-w-[1400px]">
      <header className="border-b border-ink-500 pb-6">
        <div className="label">LAB · L3</div>
        <h1 className="font-serif text-[42px] leading-[1.05] tracking-tight text-paper mt-1">
          Campaign Dry-Run
        </h1>
        <p className="text-paper-trace text-[13px] mt-2 font-serif max-w-[820px]">
          Compose a saved <span className="font-mono text-paper">Segment</span> with a Lab
          template's <span className="font-mono text-paper">content_selection_config</span> and
          inspect which articles would land in each sample member's email.
          Read-only — no send fires, no suppression history is written.
        </p>
      </header>

      <Panel label="Inputs" accent="amber">
        <div className="grid grid-cols-12 gap-4 items-end">
          <div className="col-span-12 md:col-span-5">
            <span className="label mb-1.5 block">Segment</span>
            <select
              value={segmentId ?? ''}
              onChange={(e) => setSegmentId(e.target.value ? Number(e.target.value) : null)}
              className="w-full bg-ink-700 border border-ink-500 px-3 py-2 font-mono text-[12px] text-paper cursor-pointer"
            >
              <option value="">— pick a segment —</option>
              {segments.data?.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name} · {s.slug}
                  {typeof s.member_count === 'number' ? ` · ${s.member_count.toLocaleString()} members` : ''}
                </option>
              ))}
            </select>
          </div>

          <div className="col-span-12 md:col-span-4">
            <span className="label mb-1.5 block">Lab template</span>
            <select
              value={templateId ?? ''}
              onChange={(e) => setTemplateId(e.target.value ? Number(e.target.value) : null)}
              className="w-full bg-ink-700 border border-ink-500 px-3 py-2 font-mono text-[12px] text-paper cursor-pointer"
            >
              <option value="">— pick a template —</option>
              {templates.data?.map((t) => (
                <option key={t.id} value={t.id} disabled={!t.has_selection_config}>
                  {t.name}
                  {t.has_selection_config ? '' : ' · (no selection config)'}
                </option>
              ))}
            </select>
          </div>

          <div className="col-span-6 md:col-span-2">
            <span className="label mb-1.5 block">Sample size</span>
            <input
              type="number"
              min={1}
              max={10}
              value={sampleSize}
              onChange={(e) => {
                const n = Number(e.target.value)
                if (Number.isFinite(n)) setSampleSize(Math.max(1, Math.min(10, n)))
              }}
              className="w-full bg-ink-700 border border-ink-500 px-3 py-2 font-mono text-[13px] text-paper"
            />
          </div>

          <div className="col-span-6 md:col-span-1">
            <Button
              variant="primary"
              disabled={!canSubmit || dryRun.isFetching}
              onClick={() =>
                setSubmitted({ segmentId: segmentId!, templateId: templateId!, sampleSize })
              }
            >
              {dryRun.isFetching ? '…' : 'RUN'}
            </Button>
          </div>
        </div>

        {dryRun.error && (
          <div className="mt-4 font-mono text-[12px] text-radar-red border border-radar-red/40 bg-radar-red/10 p-3">
            {String((dryRun.error as { detail?: unknown }).detail ?? dryRun.error)}
          </div>
        )}
      </Panel>

      {dryRun.data && (
        <Result
          data={dryRun.data}
          articles={articleMap}
          articlesLoading={articleMap.size === 0 && articleIds.length > 0}
        />
      )}
    </div>
  )
}

// ---------- Result ----------

function Result({
  data, articles, articlesLoading,
}: {
  data: DryRunOut
  articles: Map<number, ArticleOut>
  articlesLoading: boolean
}) {
  return (
    <div className="space-y-6">
      <Panel label="Audience" accent="cyan">
        <div className="flex items-baseline gap-6">
          <div>
            <div className="label">Matching members</div>
            <div className="font-serif text-[36px] text-paper tabular-nums">
              {data.audience_size.toLocaleString()}
            </div>
          </div>
          <div>
            <div className="label">Showing sample</div>
            <div className="font-serif text-[24px] text-paper tabular-nums">
              {data.sample.length}
            </div>
          </div>
          <div className="ml-auto text-paper-trace font-mono text-[11px]">
            sample is the first N audience members ordered by id — re-runs are reproducible
          </div>
        </div>
      </Panel>

      <Panel label={`Per-member preview · ${data.sample.length}`} accent="green" bodyClassName="p-0">
        {data.sample.length === 0 ? (
          <div className="label text-paper-trace py-8 text-center">
            Audience is empty — no members would receive this campaign
          </div>
        ) : (
          <div className="divide-y divide-ink-500">
            {data.sample.map((m) => (
              <MemberRow key={m.subscriber_id} member={m} articles={articles} loading={articlesLoading} />
            ))}
          </div>
        )}
      </Panel>
    </div>
  )
}

function MemberRow({
  member, articles, loading,
}: {
  member: DryRunMember
  articles: Map<number, ArticleOut>
  loading: boolean
}) {
  return (
    <div className="p-4">
      <div className="flex items-baseline gap-3 mb-3">
        <span className="font-mono text-[11px] tracking-label text-amber-glow tabular-nums">
          SUB {member.subscriber_id}
        </span>
        <span className="font-serif text-paper text-[14px]">{member.email}</span>
        <span className="label text-paper-trace ml-auto">
          {member.article_ids.length} article{member.article_ids.length === 1 ? '' : 's'}
        </span>
      </div>
      {member.article_ids.length === 0 ? (
        <div className="label text-paper-trace py-2">
          No articles selected — every candidate was suppressed for this member,
          or none match the template's filter.
        </div>
      ) : loading ? (
        <div className="label text-paper-trace animate-pulse py-3">LOADING ARTICLES</div>
      ) : (
        <div className="space-y-2">
          {member.article_ids.map((id) => {
            const a = articles.get(id)
            if (!a) {
              return (
                <div key={id} className="font-mono text-[11px] text-paper-trace">
                  article #{id} (not yet loaded)
                </div>
              )
            }
            return <ArticleCard key={id} article={a} />
          })}
        </div>
      )}
    </div>
  )
}

function ArticleCard({ article: a }: { article: ArticleOut }) {
  return (
    <div className="flex gap-3 border border-ink-500/60 p-2">
      {a.featured_image_url ? (
        <img
          src={a.featured_image_url}
          alt=""
          className="w-20 h-14 object-cover border border-paper-trace flex-shrink-0"
          onError={(e) => { (e.currentTarget as HTMLImageElement).style.visibility = 'hidden' }}
        />
      ) : (
        <div className="w-20 h-14 border border-paper-trace bg-paper-deepest flex-shrink-0" />
      )}
      <div className="flex-1 min-w-0">
        <Link
          to={`/lab/archive/${a.id}`}
          className="font-serif text-paper text-[13px] leading-tight hover:text-amber-glow"
        >
          {a.title}
        </Link>
        <div className="mt-0.5 flex flex-wrap gap-2 items-center">
          <span className="label text-paper-trace">
            {PUB_NAMES[a.publication_slug] ?? a.publication_slug}
          </span>
          {a.story_type && (
            <span className="font-mono text-[10px] tracking-label text-radar-cyan uppercase">
              {a.story_type}
            </span>
          )}
          {a.era && (
            <span className="font-mono text-[10px] tracking-label text-paper-trace">
              · {a.era}
            </span>
          )}
        </div>
        {a.short_teaser && (
          <div className="mt-1 font-serif text-[12px] text-paper-trace leading-snug line-clamp-2">
            {a.short_teaser}
          </div>
        )}
      </div>
    </div>
  )
}

// ---------- Article hydration ----------
//
// One bulk fetch via /api/lab/archive/by-ids; cheap one-query lookup.

function useArticleMap(ids: number[]): Map<number, ArticleOut> {
  const sortedKey = useMemo(() => [...ids].sort((a, b) => a - b).join(','), [ids])

  const articles = useQuery({
    queryKey: ['lab', 'archive', 'by-ids', sortedKey],
    queryFn: () =>
      api.get<ArticleOut[]>(`/api/lab/archive/by-ids?ids=${encodeURIComponent(sortedKey)}`),
    enabled: ids.length > 0,
    staleTime: 60_000,
  })

  return useMemo(() => {
    const m = new Map<number, ArticleOut>()
    if (articles.data) for (const it of articles.data) m.set(it.id, it)
    return m
  }, [articles.data])
}
